import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Paper,
  Avatar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  Business,
  Schedule,
  Support,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import getInTouchData from '../data/contact/getInTouchData.json';
import frequentlyAskedQstnsData from '../data/contact/frequentlyAskedQstnsData.json';


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const getInTouchDataiconMap = {
    Email: <Email sx={{ fontSize: 40 }} />,
    Phone: <Phone sx={{ fontSize: 40 }} />,
    LocationOn: <LocationOn sx={{ fontSize: 40 }} />
  };

  const services = [
    'SAP Staffing Solutions',
    'Power BI Analytics',
    'Azure Cloud Solutions',
    'SAP Analytics Cloud',
    'General Inquiry',
    'Partnership Opportunity',
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }

    // Here you would typically send the form data to your backend
    console.log('Contact form submitted:', formData);

    setShowSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      service: '',
      message: '',
    });

    setTimeout(() => setShowSuccess(false), 5000);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                mb: 4,
              }}
            >
              Let's Discuss Your IT Talent Requirements
            </Typography>
            <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
              "Ready to deliver the right talent for your success"
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Information Cards */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Get In Touch
        </Typography>
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {getInTouchData.map((info, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent>
                    <Avatar
                      sx={{
                        bgcolor: info.color,
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                      }}
                    >
                      {getInTouchDataiconMap[info.icon]}
                    </Avatar>
                    <Typography variant="h5" component="h3" gutterBottom fontWeight="bold">
                      {info.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="primary"
                      gutterBottom
                      fontWeight="bold"
                    >
                      {info.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {info.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Contact Form and Company Info */}
        <Grid container spacing={6}>
          {/* Contact Form */}
          <Grid item xs={12} lg={8}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Send Us a Message
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Fill out the form below and we'll get back to you within 24 hours.
                </Typography>

                {showSuccess && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Thank you for your message! We'll get back to you soon.
                  </Alert>
                )}

                {showError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    Please fill in all required fields.
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name *"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address *"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Service of Interest</InputLabel>
                        <Select
                          name="service"
                          value={formData.service}
                          onChange={handleInputChange}
                          label="Service of Interest"
                        >
                          {services.map((service, index) => (
                            <MenuItem key={index} value={service}>
                              {service}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Message *"
                        name="message"
                        multiline
                        rows={6}
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your requirements, project timeline, and how we can help you..."
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={<Send />}
                        sx={{
                          py: 1.5,
                          px: 4,
                          fontSize: '1.1rem',
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </motion.div>
          </Grid>

          {/* Company Information */}
          <Grid item xs={12} lg={4}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Company Information
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    VinPriv Talent Solutions Private Limited
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Founded: February 2024
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Industry: IT Staffing and Talent Solutions
                  </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Core Expertise:
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Business sx={{ mr: 1, fontSize: 16 }} /> SAP Solutions
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Business sx={{ mr: 1, fontSize: 16 }} /> Power BI Analytics
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Business sx={{ mr: 1, fontSize: 16 }} /> Microsoft Azure
                  </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Business Hours:
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Schedule sx={{ mr: 1, fontSize: 16 }} /> Monday - Friday: 9:00 AM - 6:00 PM IST
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Schedule sx={{ mr: 1, fontSize: 16 }} /> Saturday: 10:00 AM - 2:00 PM IST
                </Typography>

                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Response Time:
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Support sx={{ mr: 1, fontSize: 16 }} /> We typically respond within 24 hours
                </Typography>
              </Paper>

              {/* Quick Stats */}
              <Paper elevation={3} sx={{ p: 4, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Why Choose VinPriv?
                </Typography>
                <Grid container spacing={2} textAlign="center">
                  <Grid item xs={6}>
                    <Typography variant="h4" fontWeight="bold">
                      25+
                    </Typography>
                    <Typography variant="body2">Expert Consultants</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" fontWeight="bold">
                      2
                    </Typography>
                    <Typography variant="body2">Projects Delivered</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" fontWeight="bold">
                      95%
                    </Typography>
                    <Typography variant="body2">Client Satisfaction</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h4" fontWeight="bold">
                      48hrs
                    </Typography>
                    <Typography variant="body2">Avg. Response Time</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{
              color: 'text.primary',
              fontWeight: 'bold',
              mb: 6,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px',
              },
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              whiteSpace: 'nowrap',
              backgroundColor: '#f5f5f5',
              borderRadius: 2,
              px: 2,
              py: 2,
              gap: 4,
              scrollbarColor: '#1976d2 #e0e0e0',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#1976d2',
                borderRadius: '4px',
              },
            }}
          >
            {frequentlyAskedQstnsData.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ scrollSnapAlign: 'start', minWidth: 300, flex: '0 0 auto' }}
              >
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    fontWeight="bold"
                    color="primary"
                  >
                    {faq.question}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>


      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 300 }}>
            Let's discuss how we can help you find the right talent for your next project
          </Typography>
          <Typography variant="body1">
            Contact us today and experience the VinPriv difference!
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;